import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Controller, FormProvider, useForm, useFieldArray, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2, Plus, ArrowLeft } from 'lucide-react';
import useTenantState from '@/states/tenant.state';
import useKpiTemplateState from '@/states/kpiTemplate.state';
import useKeyActivityState, { IKeyActivity } from '@/states/key-activities.state';
import useJobDescriptionState, { IJobDescription } from '@/states/job.state';
import { useGetOrgChartNames } from '@/pages/company-profile/sop/useSopMedia';
import { getKpiTemplateChildRoles } from '@/api/kpiTemplate';
import { useOrgChartContext } from '@/contexts/OrgChartContext';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { kpiTemplateFormSchema, KpiTemplateFormValues, defaultKpiItem } from './kpi-template-form.schema';

// Individual KPI item card with its own cascade state
function KpiItemCard({
  index,
  onRemove,
  orgChartNodeId,
  canDelete,
  isView,
}: {
  index: number;
  onRemove: () => void;
  orgChartNodeId: number;
  canDelete: boolean;
  isView?: boolean;
}) {
  const {
    register,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<KpiTemplateFormValues>();
  const { currentTenant } = useTenantState();
  const { getKeyActivities } = useKeyActivityState();
  const { getJobDescriptions } = useJobDescriptionState();

  const [keyActivityOptions, setKeyActivityOptions] = useState<IKeyActivity[]>([]);
  const [jobDescOptions, setJobDescOptions] = useState<IJobDescription[]>([]);

  const selectedKeyActivityId = watch(`items.${index}.org_key_activity_id`);

  // Load key activities when org_chart_node_id changes
  useEffect(() => {
    if (!currentTenant?.id || !orgChartNodeId) {
      setKeyActivityOptions([]);
      return;
    }
    getKeyActivities(currentTenant.id, { org_chart_node_id: orgChartNodeId, page_size: 100 }).then(res => {
      setKeyActivityOptions(res?.content ?? []);
    });
  }, [currentTenant?.id, orgChartNodeId, getKeyActivities]);

  // Load job descriptions when key activity changes
  useEffect(() => {
    if (!currentTenant?.id || !selectedKeyActivityId) {
      setJobDescOptions([]);
      setValue(`items.${index}.org_job_description_id`, 0);
      setValue(`items.${index}.measurement`, '');
      return;
    }
    getJobDescriptions(currentTenant.id, { org_key_activity_id: selectedKeyActivityId, page_size: 100 }).then(res => {
      setJobDescOptions(res?.content ?? []);
    });
  }, [currentTenant?.id, selectedKeyActivityId, getJobDescriptions, index, setValue]);

  const jobDescValue = watch(`items.${index}.org_job_description_id`);

  return (
    <div className="p-4 rounded-xl border border-gray-200 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-700">KPI #{index + 1}</h4>
        {canDelete && !isView && (
          <button
            type="button"
            onClick={onRemove}
            className="p-2 rounded-xl transition-colors text-red-500 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Key Activities */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Key Activities <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedKeyActivityId ? String(selectedKeyActivityId) : ''}
            onChange={e => {
              const newKeyActivityId = e.target.value ? Number(e.target.value) : 0;
              setValue(`items.${index}.org_key_activity_id`, newKeyActivityId);
              setValue(`items.${index}.org_job_description_id`, 0);
              setValue(`items.${index}.measurement`, '');
            }}
            disabled={!orgChartNodeId || isView}
            className="text-sm w-full h-[38px] px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none m-0 leading-tight disabled:bg-gray-100 disabled:text-gray-500"
            aria-label="Key Activities"
          >
            <option value="" disabled hidden>
              Pilih Key Activities
            </option>
            {keyActivityOptions.map(ka => (
              <option key={ka.id} value={ka.id}>
                {ka.name ?? ''}
              </option>
            ))}
          </select>
          {errors.items?.[index]?.org_key_activity_id && (
            <p className="text-sm text-red-500 mt-1">{errors.items[index]?.org_key_activity_id?.message}</p>
          )}
        </div>

        {/* Uraian Tugas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Uraian Tugas <span className="text-red-500">*</span>
          </label>
          <select
            value={jobDescValue ? String(jobDescValue) : ''}
            onChange={e => {
              const nextJobDescId = e.target.value ? Number(e.target.value) : 0;
              setValue(`items.${index}.org_job_description_id`, nextJobDescId);

              const selectedJobDescOption = jobDescOptions.find(jd => Number(jd.id) === nextJobDescId);
              setValue(`items.${index}.measurement`, selectedJobDescOption?.kpi_measurement ?? '');
            }}
            disabled={!selectedKeyActivityId || isView}
            className="text-sm w-full h-[38px] px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none m-0 leading-tight disabled:bg-gray-100 disabled:text-gray-500"
            aria-label="Uraian Tugas"
          >
            <option value="" disabled hidden>
              Pilih uraian tugas
            </option>
            {jobDescOptions.map(jd => (
              <option key={jd.id} value={jd.id}>
                {jd.name ?? ''}
              </option>
            ))}
          </select>
          {errors.items?.[index]?.org_job_description_id && (
            <p className="text-sm text-red-500 mt-1">{errors.items[index]?.org_job_description_id?.message}</p>
          )}
        </div>

        {/* Bobot */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bobot (%) <span className="text-red-500">*</span>
          </label>
          <input
            {...register(`items.${index}.weight`, {
              setValueAs: (value: string) => (value === '' ? '' : Number(value)),
            })}
            type="number"
            min={1}
            max={100}
            disabled={isView}
            placeholder="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-500"
          />
          {errors.items?.[index]?.weight && (
            <p className="text-sm text-red-500 mt-1">{errors.items[index]?.weight?.message}</p>
          )}
        </div>

        {/* Target KPI */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target KPI <span className="text-red-500">*</span>
          </label>
          <input
            {...register(`items.${index}.target`, {
              setValueAs: (value: string) => (value === '' ? '' : Number(value)),
            })}
            type="number"
            min={1}
            disabled={isView}
            placeholder="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-500"
          />
          {errors.items?.[index]?.target && (
            <p className="text-sm text-red-500 mt-1">{errors.items[index]?.target?.message}</p>
          )}
        </div>
      </div>

      {/* Measurement */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Pengukuran</label>
        <Controller
          control={control}
          name={`items.${index}.measurement`}
          render={({ field }) => (
            <RichTextEditor
              value={field.value ?? ''}
              onChange={field.onChange}
              disabled={isView}
              placeholder="Pengukuran otomatis dari job description dan dapat disesuaikan..."
            />
          )}
        />
      </div>
    </div>
  );
}

export default function KpiTemplateForm() {
  const { tenant_id, id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const isView = window.location.pathname.endsWith('/view');
  const { currentTenant } = useTenantState();
  const { isLoading: orgChartLoading } = useOrgChartContext();
  const { getTemplateDetail, createTemplate, updateTemplate, isLoading } = useKpiTemplateState();
  const { data: orgChartNames = [] } = useGetOrgChartNames(currentTenant?.id);
  const [orgChartOptions, setOrgChartOptions] = useState<{ label: string; value: string }[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const pendingDataRef = useRef<KpiTemplateFormValues | null>(null);

  const formControl = useForm<KpiTemplateFormValues>({
    resolver: zodResolver(kpiTemplateFormSchema),
    defaultValues: {
      template_name: '',
      org_chart_node_id: 0,
      start_date: '',
      end_date: '',
      is_published: false,
      items: [{ ...defaultKpiItem }],
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = formControl;

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  const orgChartNodeId = watch('org_chart_node_id');
  const isPublishedValue = watch('is_published');
  const items = watch('items');
  const totalWeight = items.reduce((sum, item) => sum + (item.weight || 0), 0);
  const isWeightInvalid = isPublishedValue && totalWeight !== 100;
  const fallbackOrgChartOptions = useMemo(
    () => orgChartNames.map(n => ({ label: n.name, value: String(n.id) })),
    [orgChartNames],
  );
  const fallbackOrgChartOptionsRef = useRef(fallbackOrgChartOptions);
  fallbackOrgChartOptionsRef.current = fallbackOrgChartOptions;

  // Load existing data for edit
  useEffect(() => {
    if (!isEdit || !currentTenant?.id || !id) return;
    getTemplateDetail(currentTenant.id, id).then(data => {
      if (!data) return;
      reset({
        template_name: data.template_name ?? data.name ?? '',
        org_chart_node_id: data.org_chart_node?.id ?? data.org_chart_node_id ?? 0,
        start_date: data.start_date ?? '',
        end_date: data.end_date ?? '',
        is_published: data.is_published ?? false,
        items:
          data.items && data.items.length > 0
            ? data.items.map(item => ({
                org_key_activity_id: item.org_key_activity_id,
                org_job_description_id: item.org_job_description_id,
                weight: item.weight,
                target: item.target,
                measurement: item.measurement ?? '',
              }))
            : [{ ...defaultKpiItem }],
      });
    });
  }, [isEdit, currentTenant?.id, id, getTemplateDetail, reset]);

  // Fetch role options from KPI template API (children roles).
  useEffect(() => {
    if (!currentTenant?.id || orgChartLoading) return;

    let isMounted = true;
    getKpiTemplateChildRoles(currentTenant.id)
      .then(roles => {
        if (!isMounted) return;
        if (roles.length > 0) {
          setOrgChartOptions(roles.map(role => ({ label: role.name, value: String(role.id) })));
        } else {
          setOrgChartOptions(fallbackOrgChartOptionsRef.current);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setOrgChartOptions(fallbackOrgChartOptionsRef.current);
      });

    return () => {
      isMounted = false;
    };
  }, [currentTenant?.id, orgChartLoading]);

  const handleOrgChartChange = (value: string) => {
    if (!value) return; // ignore empty value — field is required
    const newId = Number(value);
    if (newId === orgChartNodeId) return; // same value, nothing to do
    setValue('org_chart_node_id', newId);
    // clear items because key activities depend on the selected role
    setValue('items', [{ ...defaultKpiItem }]);
  };

  const doSubmit = useCallback(
    async (data: KpiTemplateFormValues) => {
      if (!currentTenant?.id) return;
      const { template_name, ...rest } = data;

      const MONTHS = [
        'Januari',
        'Februari',
        'Maret',
        'April',
        'Mei',
        'Juni',
        'Juli',
        'Agustus',
        'September',
        'Oktober',
        'November',
        'Desember',
      ];

      const startIdx = Number(data.start_date.split('-')[1]) - 1;
      const endIdx = Number(data.end_date.split('-')[1]) - 1;
      const year = data.start_date.split('-')[0];

      // Create 'MonthStart - MonthEnd Year' period string
      const period = `${MONTHS[startIdx]} - ${MONTHS[endIdx]} ${year}`;

      const activeStatus: 'ACTIVE' | 'DRAFT' = data.is_published ? 'ACTIVE' : 'DRAFT';
      const payload = {
        ...rest,
        name: template_name,
        period,
        status: activeStatus,
        status_kpi: activeStatus,
      };

      if (isEdit && id) {
        await updateTemplate(currentTenant.id, id, payload);
      } else {
        await createTemplate(currentTenant.id, payload);
      }
      navigate(`/${tenant_id}/kpi-template`);
    },
    [currentTenant?.id, isEdit, id, updateTemplate, createTemplate, navigate, tenant_id],
  );

  const onSubmit = (data: KpiTemplateFormValues) => {
    // Show confirmation modal when publishing with an active period (start_date <= today)
    if (data.is_published) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(data.start_date);
      if (startDate <= today) {
        pendingDataRef.current = data;
        setShowConfirmModal(true);
        return;
      }
    }
    doSubmit(data);
  };

  const handleConfirmedSubmit = async () => {
    if (!pendingDataRef.current) return;
    setShowConfirmModal(false);
    await doSubmit(pendingDataRef.current);
    pendingDataRef.current = null;
  };

  const selectedOrgChartFallback = orgChartNames.find(n => n.id === orgChartNodeId);
  const effectiveOrgChartOptions = useMemo(() => {
    if (!selectedOrgChartFallback) return orgChartOptions;
    const alreadyExists = orgChartOptions.some(opt => Number(opt.value) === selectedOrgChartFallback.id);
    return alreadyExists
      ? orgChartOptions
      : [...orgChartOptions, { label: selectedOrgChartFallback.name, value: String(selectedOrgChartFallback.id) }];
  }, [orgChartOptions, selectedOrgChartFallback]);

  return (
    <section className="w-full">
      {/* Page Header */}
      <div className="p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/${tenant_id}/kpi-template`)}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-semibold text-lg">
              {isView ? 'Detail Template KPI' : isEdit ? 'Edit Template KPI' : 'Buat Template KPI'}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Template KPI &gt; {isView ? 'Detail Template' : isEdit ? 'Edit Template' : 'Buat Template'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">Publikasi</span>
          <button
            type="button"
            onClick={() => !isView && setValue('is_published', !isPublishedValue)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isPublishedValue ? 'bg-blue-600' : 'bg-gray-300'
            } ${isView ? 'cursor-not-allowed opacity-70' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isPublishedValue ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="mx-5 border-b border-gray-200" />

      <FormProvider {...formControl}>
        <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-6">
          {/* Template Info */}
          <div className="p-4 rounded-xl border border-gray-200 space-y-4">
            <h3 className="font-medium text-gray-800">Informasi Template</h3>

            {/* Nama Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Template <span className="text-red-500">*</span>
              </label>
              <input
                {...register('template_name')}
                type="text"
                disabled={isView}
                placeholder="Masukkan nama template..."
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-500"
              />
              {errors.template_name && <p className="text-sm text-red-500 mt-1">{errors.template_name.message}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Peran */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Peran <span className="text-red-500">*</span>
                </label>
                <select
                  value={orgChartNodeId ? String(orgChartNodeId) : ''}
                  onChange={e => handleOrgChartChange(e.target.value)}
                  disabled={isEdit || isView}
                  className={`text-sm w-full h-[38px] px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none m-0 leading-tight ${
                    isEdit || isView ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                  }`}
                  aria-label="Peran"
                >
                  <option value="" disabled hidden>
                    Pilih peran
                  </option>
                  {effectiveOrgChartOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.org_chart_node_id && (
                  <p className="text-sm text-red-500 mt-1">{errors.org_chart_node_id.message}</p>
                )}
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tanggal Mulai <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('start_date')}
                  type="date"
                  disabled={isView}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                />
                {errors.start_date && <p className="text-sm text-red-500 mt-1">{errors.start_date.message}</p>}
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tanggal Selesai <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('end_date')}
                  type="date"
                  disabled={isView}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                />
                {errors.end_date && <p className="text-sm text-red-500 mt-1">{errors.end_date.message}</p>}
              </div>
            </div>
          </div>

          {/* KPI Items */}
          <div className="p-4 rounded-xl border border-gray-200 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-800">Daftar KPI</h3>
              {typeof errors.items?.message === 'string' && (
                <p className="text-sm text-red-500">{errors.items.message}</p>
              )}
            </div>

            {fields.map((field, index) => (
              <KpiItemCard
                key={field.id}
                index={index}
                orgChartNodeId={orgChartNodeId}
                onRemove={() => remove(index)}
                canDelete={fields.length > 1}
                isView={isView}
              />
            ))}

            {!isView && (
              <button
                type="button"
                onClick={() => append({ ...defaultKpiItem })}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-xl hover:bg-blue-50 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Tambah KPI
              </button>
            )}
          </div>

          {/* Footer */}
          {!isView && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="space-y-0.5">
                <div className="text-sm font-medium text-gray-700">
                  Total Bobot:{' '}
                  <span className={totalWeight !== 100 ? 'text-red-500' : 'text-green-600'}>{totalWeight}%</span>
                </div>
                {isWeightInvalid && (
                  <p className="text-xs text-red-500">Total bobot harus 100% untuk mempublikasikan template.</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/${tenant_id}/kpi-template`)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isLoading || isWeightInvalid}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </div>
          )}
        </form>
      </FormProvider>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Konfirmasi Publikasi Template KPI</h2>
            <div className="space-y-2 text-sm text-gray-600">
              <p>Anda akan menyimpan template KPI dengan publikasi aktif pada periode yang sedang berjalan.</p>
              <p className="font-medium text-gray-800">Setelah disimpan, template tidak dapat diedit kembali.</p>
              <p>Pastikan data KPI yang dimasukkan sudah benar.</p>
              <p>Apakah Anda ingin melanjutkan?</p>
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleConfirmedSubmit}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Menyimpan...' : 'Simpan & Publikasikan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
